import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { responseSpeedLabel } from './SalesRepDashboard';
import { toast } from 'sonner';
import { BrainTemplatePicker } from '../components/demo/BrainTemplatePicker';
import { SaveBrainButton } from '../components/demo/SaveBrainButton';
import { SavedBrains } from '../components/demo/SavedBrains';
import { RepLeaderboard } from '../components/demo/RepLeaderboard';
import { ConnectionRequests } from '../components/admin/ConnectionRequests';
import { BusinessNumberCell } from '../components/admin/BusinessNumberCell';
import { 
  Users, Building2, Phone, MessageSquare, DollarSign, Brain, 
  TrendingUp, Trash2, Settings, LogOut, RefreshCw,
  Crown, Shield, ChevronDown, X, Eye, Edit2, ExternalLink,
  Mail, MapPin, Calendar, Clock, CreditCard, Bot, Send, Minimize2, Maximize2,
  Volume2, Download, Mic, Globe, Plus, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  
  // Customer profits state
  const [customerProfits, setCustomerProfits] = useState(null);
  const [customersAtRisk, setCustomersAtRisk] = useState(null);
  const [pendingPayments, setPendingPayments] = useState(null);
  
  // Detail view states
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Phone number assignment states
  const [assignPhoneNumber, setAssignPhoneNumber] = useState('');
  const [assigningPhone, setAssigningPhone] = useState(false);
  
  // Alert sending state
  const [sendingAlert, setSendingAlert] = useState(false);

  // Admin Pro Chatbot states
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m your Admin Assistant. I can help you understand the platform stats, answer questions about businesses, or assist with common admin tasks. What would you like to know?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Demo Mode states
  const [demoAgents, setDemoAgents] = useState([]);
  const [selectedDemoAgent, setSelectedDemoAgent] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  
  // Sales Demo Builder states
  const [salesDemoStep, setSalesDemoStep] = useState(1);
  const [salesDemoCompanyName, setSalesDemoCompanyName] = useState('');
  const [salesDemoWebsite, setSalesDemoWebsite] = useState('');
  const [salesDemoData, setSalesDemoData] = useState(null);
  const [salesDemoGenerating, setSalesDemoGenerating] = useState(false);
  const [salesDemoAgentId, setSalesDemoAgentId] = useState(null);
  const [salesDemoConnecting, setSalesDemoConnecting] = useState(false);
  const [salesDemoConnected, setSalesDemoConnected] = useState(false);
  const [customerTransferNumber, setCustomerTransferNumber] = useState('');
  const [savingTransferNumber, setSavingTransferNumber] = useState(false);
  
  // Voice configuration states - fetched from Retell API
  const [selectedVoice, setSelectedVoice] = useState('11labs-Lily');
  const [selectedAccent, setSelectedAccent] = useState('All');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedBackground, setSelectedBackground] = useState('office');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);
  const [voiceTemperature, setVoiceTemperature] = useState(1.0);
  const [responsiveness, setResponsiveness] = useState(0.5);
  const [interruptionSensitivity, setInterruptionSensitivity] = useState(0.6);
  const [voiceSearch, setVoiceSearch] = useState('');
  
  // Voice data from API
  const [allVoices, setAllVoices] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [allBackgrounds, setAllBackgrounds] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(null);
  const audioRef = useRef(null);

  // Sales Rep management states
  const [salesReps, setSalesReps] = useState([]);
  const [showCreateSalesRep, setShowCreateSalesRep] = useState(false);
  const [newSalesRep, setNewSalesRep] = useState({ email: '', password: '', name: '' });
  const [creatingSalesRep, setCreatingSalesRep] = useState(false);

  // Fetch voices from Retell API
  const fetchVoices = async () => {
    setVoicesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/demo/voices`);
      if (res.ok) {
        const data = await res.json();
        setAllVoices(data.voices || []);
        setAllLanguages(data.languages || []);
        setAllBackgrounds(data.backgrounds || []);
      }
    } catch (err) {
      console.error('Failed to fetch voices:', err);
    } finally {
      setVoicesLoading(false);
    }
  };

  // Get unique accents from voices
  const uniqueAccents = ['All', ...new Set(allVoices.map(v => v.accent).filter(Boolean))];
  
  // Filter voices based on selections
  const filteredVoices = allVoices.filter(v => {
    const matchesAccent = selectedAccent === 'All' || v.accent === selectedAccent;
    const matchesGender = selectedGender === 'all' || v.gender === selectedGender;
    const matchesSearch = !voiceSearch || 
      v.name?.toLowerCase().includes(voiceSearch.toLowerCase()) ||
      v.provider?.toLowerCase().includes(voiceSearch.toLowerCase());
    return matchesAccent && matchesGender && matchesSearch;
  });

  // Play voice preview
  const playVoicePreview = (voice) => {
    if (playingPreview === voice.voice_id) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingPreview(null);
    } else {
      // Play new preview
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (voice.preview_url) {
        audioRef.current = new Audio(voice.preview_url);
        audioRef.current.play();
        audioRef.current.onended = () => setPlayingPreview(null);
        setPlayingPreview(voice.voice_id);
      }
    }
  };

  // Live demo phone number
  const LIVE_DEMO_NUMBER = '+19377444837';
  const LIVE_DEMO_NUMBER_DISPLAY = '+1 (937) 744-4837';

  // Fetch voices when component mounts or when demo mode is accessed
  useEffect(() => {
    if (activeTab === 'demo' && allVoices.length === 0) {
      fetchVoices();
    }
  }, [activeTab]);

  // Scroll to bottom of chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          message: userMessage,
          context: {
            stats: stats,
            businessCount: businesses?.length || 0,
            userCount: users?.length || 0
          }
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I couldn\'t connect to the server. Please check your connection.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Sales Demo Builder Functions
  const generateSalesDemo = async () => {
    setBrainId(null);
    if (!salesDemoCompanyName.trim()) {
      alert('Please enter a company name');
      return;
    }
    
    setSalesDemoGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/demo/analyze-business`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: salesDemoCompanyName.trim(),
          website_url: salesDemoWebsite.trim() || null,
          google_url: null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSalesDemoData(data);
        setSalesDemoStep(2);
      } else {
        alert('Failed to generate demo. Please try again.');
      }
    } catch (err) {
      console.error('Generate demo error:', err);
      alert('Failed to generate demo. Please try again.');
    } finally {
      setSalesDemoGenerating(false);
    }
  };

  const createAndConnectAgent = async () => {
    if (!salesDemoData) return;
    
    setSalesDemoConnecting(true);
    try {
      // Step 1: Create the demo agent - format data as backend expects
      const createRes = await fetch(`${API_URL}/api/demo/create-temp-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_data: {
            business_name: salesDemoData.business_name,
            category: salesDemoData.category || 'general',
            greeting: salesDemoData.greeting,
            personality: salesDemoData.personality,
            services: salesDemoData.services || [],
            hours: salesDemoData.hours || '',
            phone: salesDemoData.phone || '',
            address: salesDemoData.address || ''
          },
          voice_id: selectedVoice,
          background_noise: selectedBackground,
          voice_speed: voiceSpeed,
          voice_temperature: voiceTemperature,
          responsiveness,
          interruption_sensitivity: interruptionSensitivity,
          language: selectedLanguage,
          custom_greeting: salesDemoData.greeting,
          custom_prompt: salesDemoData.personality
        })
      });
      
      if (!createRes.ok) {
        throw new Error('Failed to create agent');
      }
      
      const agentData = await createRes.json();
      const agentId = agentData.agent_id;
      setSalesDemoAgentId(agentId);
      
      // Step 2: Connect the live phone number to this agent
      const connectRes = await fetch(`${API_URL}/api/admin/connect-demo-number`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_id: agentId,
          business_name: salesDemoData.business_name
        })
      });
      
      if (connectRes.ok) {
        setSalesDemoConnected(true);
        setSalesDemoStep(3);
      } else {
        // Agent created but phone connection failed
        setSalesDemoStep(3);
        alert('Agent created but could not connect phone number. You can still use web demo.');
      }
    } catch (err) {
      console.error('Create agent error:', err);
      alert('Failed to create agent. Please try again.');
    } finally {
      setSalesDemoConnecting(false);
    }
  };

  const saveTransferNumber = async () => {
    if (!customerTransferNumber.trim() || !salesDemoAgentId) return;
    
    setSavingTransferNumber(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/set-transfer-number`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_id: salesDemoAgentId,
          transfer_number: customerTransferNumber.trim()
        })
      });
      
      if (res.ok) {
        alert('Transfer number saved! Calls can now be transferred to: ' + customerTransferNumber);
      } else {
        alert('Failed to save transfer number. Please try again.');
      }
    } catch (err) {
      console.error('Save transfer error:', err);
      alert('Failed to save transfer number.');
    } finally {
      setSavingTransferNumber(false);
    }
  };

  const resetSalesDemo = () => {
    setSalesDemoStep(1);
    setSalesDemoCompanyName('');
    setSalesDemoWebsite('');
    setSalesDemoData(null);
    setSalesDemoAgentId(null);
    setSalesDemoConnected(false);
    setCustomerTransferNumber('');
    setBrainId(null);
  };

  const [brainId, setBrainId] = useState(null);

  const getBrainPayload = () => ({
    business_name: salesDemoCompanyName || salesDemoData?.business_name || '',
    website: salesDemoWebsite,
    greeting: salesDemoData?.greeting || '',
    prompt: salesDemoData?.personality || '',
    business_data: salesDemoData || {},
    settings: {
      voice_id: selectedVoice, background: selectedBackground, language: selectedLanguage,
      voice_speed: voiceSpeed, voice_temperature: voiceTemperature,
      responsiveness, interruption_sensitivity: interruptionSensitivity,
    },
  });

  const loadBrain = (b) => {
    const st = b.settings || {};
    setBrainId(b.id);
    setSalesDemoCompanyName(b.business_name);
    setSalesDemoWebsite(b.website || '');
    setSalesDemoData({ ...b.business_data, business_name: b.business_name, greeting: b.greeting, personality: b.prompt });
    if (st.voice_id) setSelectedVoice(st.voice_id);
    if (st.background) setSelectedBackground(st.background);
    if (st.language) setSelectedLanguage(st.language);
    if (st.voice_speed != null) setVoiceSpeed(st.voice_speed);
    if (st.voice_temperature != null) setVoiceTemperature(st.voice_temperature);
    if (st.responsiveness != null) setResponsiveness(st.responsiveness);
    if (st.interruption_sensitivity != null) setInterruptionSensitivity(st.interruption_sensitivity);
    setSalesDemoStep(2);
    toast.success(`Loaded brain for ${b.business_name}`);
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, businessesRes, usersRes, callsRes, profitsRes, atRiskRes, pendingRes, salesRepsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/businesses`, { headers }),
        fetch(`${API_URL}/api/admin/users`, { headers }),
        fetch(`${API_URL}/api/admin/calls?limit=20`, { headers }),
        fetch(`${API_URL}/api/admin/all-customer-profits`, { headers }),
        fetch(`${API_URL}/api/admin/customers-at-risk?threshold=50`, { headers }),
        fetch(`${API_URL}/api/admin/pending-payments`, { headers }),
        fetch(`${API_URL}/api/admin/sales-reps`, { headers })
      ]);

      if (!statsRes.ok) {
        if (statsRes.status === 403) {
          setError('Access denied. Admin privileges required.');
          return;
        }
        throw new Error('Failed to fetch admin data');
      }

      const [statsData, businessesData, usersData, callsData, profitsData, atRiskData, pendingData, salesRepsData] = await Promise.all([
        statsRes.json(),
        businessesRes.json(),
        usersRes.json(),
        callsRes.json(),
        profitsRes.ok ? profitsRes.json() : null,
        atRiskRes.ok ? atRiskRes.json() : null,
        pendingRes.ok ? pendingRes.json() : null,
        salesRepsRes.ok ? salesRepsRes.json() : { sales_reps: [] }
      ]);

      setStats(statsData);
      setBusinesses(businessesData);
      setUsers(usersData);
      setCalls(callsData);
      setCustomerProfits(profitsData);
      setCustomersAtRisk(atRiskData);
      setPendingPayments(pendingData);
      setSalesReps(salesRepsData.sales_reps || []);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sales Rep management functions
  const handleCreateSalesRep = async () => {
    if (!newSalesRep.email) {
      alert('Email is required');
      return;
    }
    
    setCreatingSalesRep(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/create-sales-rep`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSalesRep)
      });
      
      if (res.ok) {
        const data = await res.json();
        setSalesReps([...salesReps, data.user]);
        setNewSalesRep({ email: '', password: '', name: '' });
        setShowCreateSalesRep(false);
        alert(data.email_sent
          ? `Sales rep created. We emailed ${data.user.email} their login and a temporary password. They'll pick their own password on first sign-in.`
          : `Sales rep created, but the welcome email could not be sent (email isn't set up yet).\n\nShare these with them:\nEmail: ${data.user.email}\nTemporary password: ${data.temp_password}\n\nThey'll pick their own password on first sign-in.`);
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to create sales rep');
      }
    } catch (err) {
      alert('Failed to create sales rep');
    } finally {
      setCreatingSalesRep(false);
    }
  };

  const handleDeleteSalesRep = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this sales rep?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/sales-rep/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setSalesReps(salesReps.filter(sr => sr.id !== userId));
        alert('Sales rep deleted');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to delete sales rep');
      }
    } catch (err) {
      alert('Failed to delete sales rep');
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/business/${businessId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error deleting business:', err);
    }
  };

  const handleUpdateTier = async (businessId, tier) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/business/${businessId}/subscription?tier=${tier}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchData();
        if (selectedBusiness?.id === businessId) {
          setSelectedBusiness(prev => ({ ...prev, subscription_tier: tier }));
        }
      }
    } catch (err) {
      console.error('Error updating tier:', err);
    }
  };

  // View business details
  const handleViewBusiness = (business) => {
    setSelectedBusiness(business);
    setEditData(business);
    setEditMode(false);
  };

  // View user details
  const handleViewUser = (user) => {
    setSelectedUser(user);
  };

  // View call details
  const handleViewCall = (call) => {
    setSelectedCall(call);
  };

  // Save business edits
  const handleSaveBusiness = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/business/${selectedBusiness.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        fetchData();
        setSelectedBusiness({ ...selectedBusiness, ...editData });
        setEditMode(false);
      }
    } catch (err) {
      console.error('Error saving business:', err);
    }
  };

  // Assign phone number to business
  const handleAssignPhoneNumber = async () => {
    if (!assignPhoneNumber || !selectedBusiness) return;
    
    setAssigningPhone(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/business/${selectedBusiness.id}/assign-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phone_number: assignPhoneNumber })
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(`Phone number ${assignPhoneNumber} assigned successfully!`);
        setAssignPhoneNumber('');
        // Refresh business data
        setSelectedBusiness({ ...selectedBusiness, retell_phone_number: assignPhoneNumber });
        fetchData();
      } else {
        const errorData = await res.json();
        alert(`Failed to assign phone: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error assigning phone:', err);
      alert('Failed to assign phone number');
    } finally {
      setAssigningPhone(false);
    }
  };

  // Send alert to admin about at-risk customers
  const handleSendAtRiskAlert = async () => {
    setSendingAlert(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-at-risk-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      if (res.ok) {
        alert(data.message);
      } else {
        alert(`Failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error sending alert:', err);
      alert('Failed to send alert');
    } finally {
      setSendingAlert(false);
    }
  };

  // Close detail modals
  const closeModal = () => {
    setSelectedBusiness(null);
    setSelectedUser(null);
    setSelectedCall(null);
    setEditMode(false);
  };

  // Verify and activate a pending payment
  const handleVerifyPayment = async (businessId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verify-payment/${businessId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      fetchData();
    } catch (err) {
      console.error('Error verifying payment:', err);
      alert('Failed to verify payment');
    }
  };

  // Force activate a subscription (use when Stripe confirms but webhook failed)
  const handleForceActivate = async (businessId) => {
    if (!window.confirm('Have you manually verified in Stripe Dashboard that this payment was successful? This will activate the subscription WITHOUT Stripe verification.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/force-activate/${businessId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      fetchData();
    } catch (err) {
      console.error('Error force activating:', err);
      alert('Failed to activate subscription');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600 text-xl flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin" />
          Loading admin panel...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Card className="bg-white border-red-200 max-w-md shadow-lg">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription className="text-red-500">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700 text-slate-900">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Admin Panel</h1>
              <p className="text-sm text-slate-500">Front Desk Pro Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" size="sm" className="text-slate-600 border-slate-300">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={logout} variant="outline" size="sm" className="text-slate-600 border-slate-300 hover:text-red-600 hover:border-red-200">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {['overview', 'demo', 'profits', 'payments', 'businesses', 'users', 'sales_reps', 'calls'].map(tab => (
              <button
                key={tab}
                data-testid={`admin-tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === tab 
                    ? 'text-blue-600 border-blue-600' 
                    : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
                } ${tab === 'payments' && pendingPayments?.count > 0 ? 'relative' : ''}`}
              >
                {tab === 'demo' ? '🎯 Demo Mode' : tab === 'sales_reps' ? '👥 Sales Reps' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'payments' && pendingPayments?.count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {pendingPayments.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Total Users</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_users}</p>
                    </div>
                    <Users className="w-10 h-10 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Total Businesses</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_businesses}</p>
                    </div>
                    <Building2 className="w-10 h-10 text-green-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Total Minutes</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.usage.total_minutes}</p>
                    </div>
                    <Phone className="w-10 h-10 text-purple-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Total Texts</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.usage.total_sms}</p>
                    </div>
                    <MessageSquare className="w-10 h-10 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue & Subscriptions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Revenue Card */}
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">Subscriptions</span>
                    <span className="text-slate-900 font-bold">${stats.revenue?.subscriptions || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">Minutes ({stats.usage?.total_minutes || 0} × ${stats.pricing?.price_per_minute || 0.25})</span>
                    <span className="text-slate-900 font-bold">${stats.revenue?.minutes || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">Texts ({stats.usage?.total_sms || 0} sent, add-on $25/mo)</span>
                    <span className="text-slate-900 font-bold">${(stats.revenue?.sms || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-green-500/10 rounded px-2 -mx-2">
                    <span className="text-green-400 font-medium">Total Revenue</span>
                    <span className="text-green-400 font-bold text-xl">${stats.revenue?.total || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Costs Card */}
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-red-400" />
                    Costs (Provider Fees)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">Minutes ({stats.usage?.total_minutes || 0} × ${stats.pricing?.cost_per_minute || 0.08})</span>
                    <span className="text-slate-900 font-bold">${stats.costs?.minutes || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">Texts ({stats.usage?.total_sms || 0} × ${stats.pricing?.cost_per_sms || 0.0075})</span>
                    <span className="text-slate-900 font-bold">${(stats.costs?.sms || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-red-500/10 rounded px-2 -mx-2">
                    <span className="text-red-400 font-medium">Total Costs</span>
                    <span className="text-red-400 font-bold text-xl">${stats.costs?.total || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Profit Card */}
              <Card className="bg-white border-slate-200 shadow-sm border-2 border-purple-500/30">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    Profit
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">From Subscriptions</span>
                    <span className="text-slate-900 font-bold">${stats.profit?.from_subscriptions || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">From Minutes <span className="text-xs text-green-400">({stats.profit?.margin_minutes})</span></span>
                    <span className="text-slate-900 font-bold">${stats.profit?.from_minutes || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">From Texts <span className="text-xs text-green-400">({stats.profit?.margin_sms})</span></span>
                    <span className="text-slate-900 font-bold">${(stats.profit?.from_sms || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-purple-500/20 rounded px-2 -mx-2">
                    <span className="text-purple-400 font-medium">Total Profit</span>
                    <span className="text-purple-400 font-bold text-xl">${stats.profit?.total || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subscriptions & Pricing Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-400" />
                    Subscriptions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">Free (Trial)</span>
                    <span className="text-slate-900 font-bold">{stats.subscriptions?.free || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-500">Pro</span>
                    <span className="text-slate-900 font-bold">{(stats.subscriptions?.starter || 0) + (stats.subscriptions?.professional || 0) + (stats.subscriptions?.enterprise || 0) + (stats.subscriptions?.pro || 0)}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-400" />
                    Text Pricing Model
                  </CardTitle>
                  <CardDescription>4 texts per appointment = $0.05 total</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-500/10 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Price per text</span>
                      <span className="text-slate-900">$25/mo add-on</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Cost per text (Twilio)</span>
                      <span className="text-slate-900">${stats.pricing?.cost_per_sms || 0.0075}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-blue-500/30 pt-2 mt-2">
                      <span className="text-slate-500">Profit per text</span>
                      <span className="text-green-400 font-medium">$0.005</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 space-y-1">
                    <p>• 1 text: Appointment confirmation to customer</p>
                    <p>• 2 text: Appointment confirmation to owner</p>
                    <p>• 3 text: Reminder to customer</p>
                    <p>• 4 text: Reminder to owner</p>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-green-500/10 rounded px-3">
                    <span className="text-slate-600">Est. Appointments</span>
                    <span className="text-green-400 font-bold">{stats.usage?.appointments_estimated || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Demo Mode Tab */}
        {activeTab === 'demo' && (
          <div className="space-y-8 pb-12">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">🎯 Sales Demo Builder</h2>
              <p className="text-slate-600 text-lg">Create a custom Pro demo for any business on the spot. Connect your live phone number instantly.</p>
            </div>

            {/* Live Demo Phone Number - Always Visible */}
            <Card className="bg-gradient-to-r from-violet-600/20 to-purple-600/20 border-2 border-violet-500/50">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 rounded-full bg-violet-500/20 flex items-center justify-center">
                      <Phone className="w-7 h-7 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm mb-1">Your Live Demo Number</p>
                      <p className="text-3xl font-bold text-slate-900 tracking-wide">{LIVE_DEMO_NUMBER_DISPLAY}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      size="lg"
                      className="bg-violet-600 hover:bg-violet-700"
                      onClick={() => {
                        navigator.clipboard.writeText(LIVE_DEMO_NUMBER);
                        alert('Number copied!');
                      }}
                    >
                      Copy Number
                    </Button>
                    <Button 
                      size="lg"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => window.open(`tel:${LIVE_DEMO_NUMBER}`)}
                    >
                      Call Now
                    </Button>
                  </div>
                </div>
                {salesDemoConnected && salesDemoData && (
                  <div className="mt-4 pt-4 border-t border-violet-500/30">
                    <p className="text-green-400 text-base flex items-center gap-2">
                      <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                      Currently connected to: <span className="font-semibold text-slate-900">{salesDemoData.business_name}</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step-by-Step Demo Builder */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-slate-900 text-xl">Build Demo for Customer</CardTitle>
                    <CardDescription className="text-base mt-1">Step {salesDemoStep} of 3</CardDescription>
                  </div>
                  {salesDemoStep > 1 && (
                    <Button variant="outline" onClick={resetSalesDemo} className="border-gray-600 text-slate-600">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Start Over
                    </Button>
                  )}
                </div>
                {/* Progress Bar */}
                <div className="flex gap-3 mt-6">
                  <div className={`h-2 flex-1 rounded-full transition-all ${salesDemoStep >= 1 ? 'bg-green-500' : 'bg-gray-700'}`}></div>
                  <div className={`h-2 flex-1 rounded-full transition-all ${salesDemoStep >= 2 ? 'bg-green-500' : 'bg-gray-700'}`}></div>
                  <div className={`h-2 flex-1 rounded-full transition-all ${salesDemoStep >= 3 ? 'bg-green-500' : 'bg-gray-700'}`}></div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {/* Step 1: Enter Business Info */}
                {salesDemoStep === 1 && (
                  <div className="space-y-6">
                    <SavedBrains token={token} onLoad={loadBrain} showOwner />
                    <div>
                      <label className="text-sm font-medium text-slate-600 mb-2 block">Customer's Business Name *</label>
                      <Input
                        placeholder="e.g., Smith Plumbing Services"
                        value={salesDemoCompanyName}
                        onChange={(e) => setSalesDemoCompanyName(e.target.value)}
                        className="bg-slate-100 border-gray-700 text-slate-900 h-12 text-lg"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600 mb-2 block">Website URL (optional but recommended)</label>
                      <Input
                        placeholder="e.g., smithplumbing.com"
                        value={salesDemoWebsite}
                        onChange={(e) => setSalesDemoWebsite(e.target.value)}
                        className="bg-slate-100 border-gray-700 text-slate-900 h-12 text-lg"
                      />
                      <p className="text-sm text-slate-400 mt-2">Adding a website helps your Pro learn their services, hours, phone, and more</p>
                    </div>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg mt-4"
                      onClick={generateSalesDemo}
                      disabled={salesDemoGenerating || !salesDemoCompanyName.trim()}
                    >
                      {salesDemoGenerating ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Generating Pro...
                        </>
                      ) : (
                        <>
                          <Bot className="w-5 h-5 mr-2" />
                          Generate Pro Demo
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Step 2: Review Pro & Connect Phone */}
                {salesDemoStep === 2 && salesDemoData && (
                  <div className="space-y-6">
                    {/* Business Name & Badge */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-slate-900">{salesDemoData.business_name}</h3>
                      {salesDemoData.ai_enhanced && (
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                          Pro-Enhanced
                        </span>
                      )}
                    </div>

                    {/* Scraped Business Details */}
                    {(salesDemoData.services?.length > 0 || salesDemoData.hours || salesDemoData.phone || salesDemoData.address) && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                        <h4 className="text-blue-300 font-medium mb-4 flex items-center gap-2">
                          <Globe className="w-5 h-5" />
                          Scraped from Website
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {salesDemoData.phone && (
                            <div>
                              <p className="text-slate-500 text-sm">Phone</p>
                              <p className="text-slate-900 font-medium">{salesDemoData.phone}</p>
                            </div>
                          )}
                          {salesDemoData.hours && (
                            <div>
                              <p className="text-slate-500 text-sm">Hours</p>
                              <p className="text-slate-900 font-medium">{salesDemoData.hours}</p>
                            </div>
                          )}
                          {salesDemoData.address && (
                            <div className="md:col-span-2">
                              <p className="text-slate-500 text-sm">Address</p>
                              <p className="text-slate-900 font-medium">{salesDemoData.address}</p>
                            </div>
                          )}
                          {salesDemoData.services?.length > 0 && (
                            <div className="md:col-span-2">
                              <p className="text-slate-500 text-sm mb-2">Services</p>
                              <div className="flex flex-wrap gap-2">
                                {salesDemoData.services.slice(0, 10).map((service, i) => (
                                  <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-sm">
                                    {service}
                                  </span>
                                ))}
                                {salesDemoData.services.length > 10 && (
                                  <span className="px-2 py-1 text-slate-500 text-sm">+{salesDemoData.services.length - 10} more</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pro Greeting - EDITABLE */}
                    <div className="bg-slate-100/50 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-slate-500">Pro Greeting (editable):</p>
                        <span className="text-xs text-amber-400">Click to edit</span>
                      </div>
                      <textarea
                        value={salesDemoData.greeting}
                        onChange={(e) => setSalesDemoData(prev => ({ ...prev, greeting: e.target.value }))}
                        className="w-full bg-white border border-slate-300 rounded-lg p-4 text-slate-900 text-lg italic leading-relaxed resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        data-testid="admin-greeting-textarea"
                        rows={2}
                        placeholder="Enter Pro greeting..."
                      />
                    </div>
                    
                    {/* Build the Brain - EDITABLE */}
                    <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50 p-5" data-testid="admin-build-brain-section">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                          <Brain className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-slate-900">Build the Brain</h4>
                          <p className="text-xs text-slate-500">Everything your Pro knows and how it behaves. Add prices, services, policies, then connect.</p>
                        </div>
                        <SaveBrainButton token={token} getPayload={getBrainPayload} brainId={brainId} onSaved={(b) => setBrainId(b.id)} />
                      </div>
                      <BrainTemplatePicker
                        businessName={salesDemoData.business_name || salesDemoCompanyName}
                        prompt={salesDemoData.personality}
                        greeting={salesDemoData.greeting}
                        onApply={({ prompt, greeting }) => setSalesDemoData(prev => ({ ...prev, personality: prompt, greeting }))}
                      />
                      <textarea
                        value={salesDemoData.personality}
                        onChange={(e) => setSalesDemoData(prev => ({ ...prev, personality: e.target.value }))}
                        className="w-full bg-white border border-slate-300 rounded-lg p-4 text-slate-900 text-sm whitespace-pre-wrap leading-relaxed focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                        rows={14}
                        placeholder="Describe your Pro's personality, services, pricing and rules..."
                        data-testid="admin-brain-prompt-textarea"
                      />
                      <p className="text-xs text-slate-400 mt-2">Tip: add pricing like "House wash starts at $199" or "Driveway cleaning: $99-$149 depending on size"</p>
                    </div>

                    {/* Voice Configuration Section */}
                    <div className="bg-slate-100/50 rounded-xl p-5 space-y-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Volume2 className="w-5 h-5 text-violet-400" />
                          <p className="text-slate-900 font-medium">Voice Settings</p>
                        </div>
                        {voicesLoading && (
                          <RefreshCw className="w-4 h-4 text-slate-500 animate-spin" />
                        )}
                        {!voicesLoading && allVoices.length > 0 && (
                          <span className="text-xs text-slate-400">{allVoices.length} voices available</span>
                        )}
                      </div>

                      {/* Language Selection */}
                      <div>
                        <p className="text-sm text-slate-500 mb-2">Language</p>
                        <select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-lg p-3 text-slate-900 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none"
                        >
                          {allLanguages.length > 0 ? (
                            allLanguages.map(lang => (
                              <option key={lang.code} value={lang.code}>{lang.name}</option>
                            ))
                          ) : (
                            <>
                              <option value="en-US">English (US)</option>
                              <option value="es-ES">Spanish</option>
                              <option value="fr-FR">French</option>
                            </>
                          )}
                        </select>
                      </div>

                      {/* Voice Search & Filters */}
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Search voices..."
                            value={voiceSearch}
                            onChange={(e) => setVoiceSearch(e.target.value)}
                            className="flex-1 bg-white border-slate-300 text-slate-900"
                          />
                        </div>
                        
                        {/* Accent & Gender Filters */}
                        <div className="flex flex-wrap gap-2">
                          <select
                            value={selectedAccent}
                            onChange={(e) => setSelectedAccent(e.target.value)}
                            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-slate-900"
                          >
                            {uniqueAccents.map(accent => (
                              <option key={accent} value={accent}>{accent}</option>
                            ))}
                          </select>
                          <select
                            value={selectedGender}
                            onChange={(e) => setSelectedGender(e.target.value)}
                            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-slate-900"
                          >
                            <option value="all">All Genders</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                          </select>
                          <span className="text-xs text-slate-400 self-center ml-2">
                            {filteredVoices.length} voices
                          </span>
                        </div>
                      </div>

                      {/* Voice Selection Grid */}
                      <div>
                        <p className="text-sm text-slate-500 mb-2">Select Voice</p>
                        <div className="max-h-64 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-2 pr-2">
                          {filteredVoices.length > 0 ? (
                            filteredVoices.slice(0, 50).map(voice => (
                              <button
                                key={voice.voice_id}
                                onClick={() => setSelectedVoice(voice.voice_id)}
                                className={`p-3 rounded-lg text-left transition-all border relative group ${
                                  selectedVoice === voice.voice_id
                                    ? 'bg-violet-600/20 border-violet-500 text-slate-900'
                                    : 'bg-gray-700/50 border-gray-600 text-slate-600 hover:bg-gray-700'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {voice.avatar_url && (
                                    <img 
                                      src={voice.avatar_url} 
                                      alt={voice.name} 
                                      className="w-8 h-8 rounded-full object-cover"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{voice.name}</p>
                                    <p className="text-xs text-slate-500 truncate">
                                      {voice.gender} • {voice.accent} • {voice.provider}
                                    </p>
                                  </div>
                                </div>
                                {/* Play Preview Button */}
                                {voice.preview_url && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      playVoicePreview(voice);
                                    }}
                                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-600 hover:bg-violet-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    {playingPreview === voice.voice_id ? (
                                      <span className="text-xs">■</span>
                                    ) : (
                                      <span className="text-xs">▶</span>
                                    )}
                                  </button>
                                )}
                                {voice.recommended && (
                                  <span className="absolute top-1 left-1 text-xs bg-green-500/20 text-green-400 px-1 rounded">★</span>
                                )}
                              </button>
                            ))
                          ) : voicesLoading ? (
                            <p className="text-slate-400 text-sm col-span-3 text-center py-4">Loading voices...</p>
                          ) : (
                            <p className="text-slate-400 text-sm col-span-3 text-center py-4">No voices match your filters</p>
                          )}
                        </div>
                        {filteredVoices.length > 50 && (
                          <p className="text-xs text-slate-400 mt-2 text-center">
                            Showing 50 of {filteredVoices.length} voices. Use search to find more.
                          </p>
                        )}
                      </div>

                      {/* Background Sound */}
                      <div>
                        <p className="text-sm text-slate-500 mb-2">Background Sound</p>
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                          {(allBackgrounds.length > 0 ? allBackgrounds : [
                            { id: 'none', name: 'None', desc: 'Clean' },
                            { id: 'office', name: 'Office', desc: 'Professional' },
                            { id: 'coffee_shop', name: 'Coffee Shop', desc: 'Café' },
                            { id: 'call_center', name: 'Call Center', desc: 'Busy' },
                          ]).map(bg => (
                            <button
                              key={bg.id}
                              onClick={() => setSelectedBackground(bg.id)}
                              className={`p-3 rounded-lg text-center transition-all border ${
                                selectedBackground === bg.id
                                  ? 'bg-violet-600/20 border-violet-500 text-slate-900'
                                  : 'bg-gray-700/50 border-gray-600 text-slate-600 hover:bg-gray-700'
                              }`}
                            >
                              <p className="font-medium text-sm">{bg.name}</p>
                              <p className="text-xs text-slate-400">{bg.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Voice Speed & Temperature */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="flex justify-between mb-2">
                            <p className="text-sm text-slate-500">Voice Speed</p>
                            <span className="text-sm text-violet-400">{voiceSpeed.toFixed(1)}x</span>
                          </div>
                          <input
                            type="range"
                            min="0.8"
                            max="1.3"
                            step="0.1"
                            value={voiceSpeed}
                            onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))}
                            className="w-full accent-violet-500"
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>Slower</span>
                            <span>Faster</span>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-2">
                            <p className="text-sm text-slate-500">Emotion</p>
                            <span className="text-sm text-violet-400">{voiceTemperature.toFixed(1)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="1.5"
                            step="0.1"
                            value={voiceTemperature}
                            onChange={(e) => setVoiceTemperature(parseFloat(e.target.value))}
                            className="w-full accent-violet-500"
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>Calm</span>
                            <span>Expressive</span>
                          </div>
                        </div>
                        <div data-testid="admin-response-speed-control">
                          <div className="flex justify-between mb-2">
                            <p className="text-sm text-slate-500">Response Speed</p>
                            <span className="text-sm text-violet-400" data-testid="admin-response-speed-value">{(responsiveness * 100).toFixed(0)}% · {responseSpeedLabel(responsiveness)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.1"
                            max="1"
                            step="0.05"
                            value={responsiveness}
                            onChange={(e) => setResponsiveness(parseFloat(e.target.value))}
                            className="w-full accent-violet-500"
                            data-testid="admin-response-speed-slider"
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>Natural pause</span>
                            <span>Instant reply</span>
                          </div>
                        </div>
                        <div data-testid="admin-interruption-control">
                          <div className="flex justify-between mb-2">
                            <p className="text-sm text-slate-500">Interruption Sensitivity</p>
                            <span className="text-sm text-violet-400">{(interruptionSensitivity * 100).toFixed(0)}%</span>
                          </div>
                          <input
                            type="range"
                            min="0.1"
                            max="1"
                            step="0.05"
                            value={interruptionSensitivity}
                            onChange={(e) => setInterruptionSensitivity(parseFloat(e.target.value))}
                            className="w-full accent-violet-500"
                            data-testid="admin-interruption-slider"
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>Lets caller finish</span>
                            <span>Easy to interrupt</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-violet-600 hover:bg-violet-700 h-14 text-lg"
                      onClick={createAndConnectAgent}
                      disabled={salesDemoConnecting}
                    >
                      {salesDemoConnecting ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Creating Agent & Connecting Phone...
                        </>
                      ) : (
                        <>
                          <Phone className="w-5 h-5 mr-2" />
                          Create Agent & Connect Phone Number
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Step 3: Demo Ready - Call & Transfer Setup */}
                {salesDemoStep === 3 && (
                  <div className="space-y-6">
                    {/* Success Banner with Quick Edit Button */}
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Phone className="w-6 h-6 text-green-400" />
                          </div>
                          <div>
                            <p className="text-green-400 font-semibold text-lg">Demo Ready!</p>
                            <p className="text-slate-600 text-sm">
                              Pro agent for <span className="text-slate-900 font-medium">{salesDemoData?.business_name}</span>
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-gray-600 text-slate-600"
                          onClick={() => setSalesDemoStep(2)}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Edit Script
                        </Button>
                      </div>
                    </div>

                    {/* Pro Greeting Preview - Collapsible */}
                    <div className="bg-slate-100/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-slate-500">Pro Greeting:</p>
                        <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded">Live</span>
                      </div>
                      <p className="text-green-300 text-base italic">"{salesDemoData?.greeting}"</p>
                    </div>

                    {/* Prompt/Personality - Collapsible with scroll */}
                    <details className="bg-slate-100/50 rounded-xl">
                      <summary className="p-4 cursor-pointer text-sm text-slate-500 hover:text-slate-600 flex items-center justify-between">
                        <span>View Full Pro Script & Knowledge</span>
                        <ChevronDown className="w-4 h-4" />
                      </summary>
                      <div className="px-4 pb-4">
                        <div className="max-h-60 overflow-y-auto text-slate-600 text-sm whitespace-pre-wrap bg-gray-900/50 p-3 rounded-lg border border-gray-700 leading-relaxed">
                          {salesDemoData?.personality}
                        </div>
                      </div>
                    </details>

                    {/* Two Demo Options Side by Side */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Web Demo Option */}
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-2 border-green-500/50 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Mic className="w-6 h-6 text-green-400" />
                          </div>
                          <div>
                            <h4 className="text-slate-900 font-semibold text-lg">Web Demo</h4>
                            <p className="text-green-400 text-sm">Instant - No phone needed</p>
                          </div>
                        </div>
                        <p className="text-slate-600 text-sm mb-4">
                          Start a voice call right in the browser. Perfect for showing your Pro on your laptop or tablet.
                        </p>
                        <Button 
                          className="w-full bg-green-600 hover:bg-green-700 h-12 text-base"
                          onClick={() => window.open(`/try-demo?agent_id=${salesDemoAgentId}`, '_blank')}
                        >
                          <Mic className="w-5 h-5 mr-2" />
                          Start Web Demo
                        </Button>
                      </div>

                      {/* Phone Demo Option */}
                      <div className="bg-gradient-to-br from-violet-600/20 to-purple-600/20 border-2 border-violet-500/50 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                            <Phone className="w-6 h-6 text-violet-400" />
                          </div>
                          <div>
                            <h4 className="text-slate-900 font-semibold text-lg">Phone Demo</h4>
                            <p className="text-violet-400 text-sm">Call the live number</p>
                          </div>
                        </div>
                        <p className="text-slate-600 text-sm mb-4">
                          Call <span className="text-slate-900 font-mono">{LIVE_DEMO_NUMBER_DISPLAY}</span> to hear your Pro on a real phone call.
                        </p>
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-violet-600 hover:bg-violet-700 h-12"
                            onClick={() => window.open(`tel:${LIVE_DEMO_NUMBER}`)}
                          >
                            <Phone className="w-5 h-5 mr-2" />
                            Call Now
                          </Button>
                          <Button 
                            variant="outline"
                            className="border-violet-500 text-violet-300 h-12 px-4"
                            onClick={() => {
                              navigator.clipboard.writeText(LIVE_DEMO_NUMBER);
                              alert('Number copied!');
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Customer Transfer Number Section */}
                    <div className="bg-slate-100/50 rounded-xl p-6">
                      <h4 className="text-slate-900 font-semibold text-lg mb-4 flex items-center gap-2">
                        <Send className="w-5 h-5 text-amber-400" />
                        Call Transfer Setup
                      </h4>
                      <p className="text-slate-600 mb-4">
                        Enter the customer's phone number. When callers request to speak to a human, your Pro will transfer to this number.
                      </p>
                      <div className="flex gap-3">
                        <Input
                          placeholder="+1 (555) 123-4567"
                          value={customerTransferNumber}
                          onChange={(e) => setCustomerTransferNumber(e.target.value)}
                          className="bg-white border-slate-300 text-slate-900 h-12 text-lg flex-1 placeholder:text-slate-400"
                        />
                        <Button 
                          className="bg-amber-600 hover:bg-amber-700 h-12 px-6"
                          onClick={saveTransferNumber}
                          disabled={savingTransferNumber || !customerTransferNumber.trim()}
                        >
                          {savingTransferNumber ? (
                            <RefreshCw className="w-5 h-5 animate-spin" />
                          ) : (
                            'Save Transfer'
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Demo Tips */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                      <h4 className="text-blue-300 font-semibold text-base mb-3">📋 Sales Demo Script</h4>
                      <ol className="text-slate-600 space-y-2 list-decimal list-inside text-sm">
                        <li>"Let me show you how this works for <strong>YOUR</strong> business"</li>
                        <li>"I've already set it up - try it right now" <em className="text-slate-400">(hand phone or start web demo)</em></li>
                        <li>Let them talk to your Pro and be impressed</li>
                        <li>"This is what your customers would experience 24/7"</li>
                        <li>"It's $199/month - less than one missed job costs you"</li>
                      </ol>
                    </div>

                    {/* Bottom Actions */}
                    <div className="flex gap-3">
                      {/* Reconnect Phone if needed */}
                      {!salesDemoConnected && salesDemoAgentId && (
                        <Button 
                          className="flex-1 bg-violet-600 hover:bg-violet-700 h-11"
                          onClick={async () => {
                            setSalesDemoConnecting(true);
                            try {
                              const connectRes = await fetch(`${API_URL}/api/admin/connect-demo-number`, {
                                method: 'POST',
                                headers: { 
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  agent_id: salesDemoAgentId,
                                  business_name: salesDemoData.business_name
                                })
                              });
                              if (connectRes.ok) {
                                setSalesDemoConnected(true);
                                alert('Phone number connected successfully!');
                              } else {
                                alert('Failed to connect phone number. Try again.');
                              }
                            } catch (err) {
                              alert('Connection error. Try again.');
                            } finally {
                              setSalesDemoConnecting(false);
                            }
                          }}
                          disabled={salesDemoConnecting}
                        >
                          {salesDemoConnecting ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Phone className="w-4 h-4 mr-2" />
                          )}
                          Reconnect Phone
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        className={`${!salesDemoConnected && salesDemoAgentId ? 'flex-1' : 'w-full'} border-gray-600 text-slate-600 h-11`}
                        onClick={resetSalesDemo}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        New Demo
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'profits' && customerProfits && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <p className="text-slate-500 text-sm">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-400">${customerProfits.summary?.total_revenue?.toFixed(2) || '0.00'}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <p className="text-slate-500 text-sm">Total Cost</p>
                  <p className="text-2xl font-bold text-red-400">${customerProfits.summary?.total_cost?.toFixed(2) || '0.00'}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <p className="text-slate-500 text-sm">Total Profit</p>
                  <p className="text-2xl font-bold text-purple-400">${customerProfits.summary?.total_profit?.toFixed(2) || '0.00'}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <p className="text-slate-500 text-sm">Paid Customers</p>
                  <p className="text-2xl font-bold text-slate-900">{customerProfits.summary?.paid_customers || 0} / {customerProfits.summary?.total_customers || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Customers At Risk Alert */}
            {customersAtRisk && (customersAtRisk.in_overage?.length > 0 || customersAtRisk.low_balance?.length > 0) && (
              <Card className="bg-red-900/20 border-red-500/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-red-400 flex items-center gap-2">
                        ⚠️ Customers At Risk
                      </CardTitle>
                      <CardDescription className="text-red-300">
                        {customersAtRisk.in_overage?.length || 0} customers in overage, {customersAtRisk.low_balance?.length || 0} with low balance
                      </CardDescription>
                    </div>
                    <Button
                      onClick={handleSendAtRiskAlert}
                      disabled={sendingAlert}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="send-alert-btn"
                    >
                      {sendingAlert ? 'Sending...' : '📧 Email Me Alert'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {customersAtRisk.in_overage?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-red-400 font-medium mb-2">🔴 In Overage (paying $0.25/min)</h4>
                      <div className="space-y-2">
                        {customersAtRisk.in_overage.map(biz => (
                          <div key={biz.id} className="flex items-center justify-between bg-red-900/30 rounded-lg p-3">
                            <div>
                              <p className="text-slate-900 font-medium">{biz.business_name}</p>
                              <p className="text-slate-500 text-sm">{biz.email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-red-400 font-bold">{biz.overage_minutes} overage mins</p>
                              <p className="text-green-400 text-sm">+${biz.overage_charge} revenue</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {customersAtRisk.low_balance?.length > 0 && (
                    <div>
                      <h4 className="text-yellow-400 font-medium mb-2">🟡 Low Balance (under {customersAtRisk.threshold} mins)</h4>
                      <div className="space-y-2">
                        {customersAtRisk.low_balance.map(biz => (
                          <div key={biz.id} className="flex items-center justify-between bg-yellow-900/20 rounded-lg p-3">
                            <div>
                              <p className="text-slate-900 font-medium">{biz.business_name}</p>
                              <p className="text-slate-500 text-sm">{biz.email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-yellow-400 font-bold">{biz.minutes_balance} mins left</p>
                              <p className="text-slate-500 text-sm">{biz.call_minutes_used} used</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* All Customers Profit Table */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Customer Profit Breakdown</CardTitle>
                <CardDescription>All customers sorted by profit (highest first)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Business</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Tier</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Minutes</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Balance</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Overage</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Revenue</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Cost</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Profit</th>
                        <th className="text-center py-3 px-4 text-slate-500 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerProfits.customers?.map(customer => (
                        <tr key={customer.id} className="border-b border-slate-200/50 hover:bg-slate-100/30">
                          <td className="py-3 px-4">
                            <p className="text-slate-900 font-medium">{customer.business_name}</p>
                            <p className="text-slate-400 text-xs">{customer.email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              customer.tier === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                              customer.tier === 'free' ? 'bg-gray-500/20 text-slate-500' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {customer.tier === 'free' ? 'Trial' : customer.tier.charAt(0).toUpperCase() + customer.tier.slice(1)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-slate-600">{customer.minutes_used}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={customer.minutes_balance <= 0 ? 'text-red-400' : customer.minutes_balance <= 50 ? 'text-yellow-400' : 'text-slate-600'}>
                              {customer.minutes_balance}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {customer.overage_minutes > 0 ? (
                              <span className="text-green-400">{customer.overage_minutes} mins</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right text-green-400">${customer.revenue.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right text-red-400">${customer.cost.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={customer.profit >= 0 ? 'text-purple-400 font-bold' : 'text-red-400 font-bold'}>
                              ${customer.profit.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {customer.status === 'overage' && <span className="text-red-400">🔴</span>}
                            {customer.status === 'low' && <span className="text-yellow-400">🟡</span>}
                            {customer.status === 'ok' && <span className="text-green-400">🟢</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Pending Payments Alert */}
            {pendingPayments?.count > 0 ? (
              <Card className="bg-amber-50 border-amber-300">
                <CardHeader>
                  <CardTitle className="text-amber-800 flex items-center gap-2">
                    ⏳ Pending Payments Requiring Attention
                  </CardTitle>
                  <CardDescription className="text-amber-700">
                    {pendingPayments.count} payment(s) are stuck as pending. Verify in Stripe Dashboard and activate below.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingPayments.pending_payments?.map(txn => (
                      <div key={txn.session_id} className="bg-white border border-amber-200 rounded-lg p-4 flex items-center justify-between">
                        <div>
                          <p className="text-slate-900 font-medium">{txn.business_name || 'Unknown Business'}</p>
                          <p className="text-slate-500 text-sm">{txn.business_email}</p>
                          <p className="text-slate-400 text-xs mt-1">
                            Amount: ${Number(txn.amount || 0).toFixed(2)} • Created: {new Date(txn.created_at).toLocaleString()}
                          </p>
                          <p className="text-gray-600 text-xs font-mono mt-1">
                            Session: {txn.session_id?.slice(0, 30)}...
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleVerifyPayment(txn.business_id)}
                            className="bg-blue-600 hover:bg-blue-700"
                            size="sm"
                          >
                            Verify with Stripe
                          </Button>
                          <Button
                            onClick={() => handleForceActivate(txn.business_id)}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            Force Activate
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl mb-4">✅</div>
                  <h3 className="text-green-800 font-bold text-lg">No Pending Payments</h3>
                  <p className="text-green-700 text-sm">All payments have been processed successfully.</p>
                </CardContent>
              </Card>
            )}

            {/* Payment Processing Info */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Payment Processing Info</CardTitle>
              </CardHeader>
              <CardContent className="text-slate-600 space-y-4">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="text-blue-400 font-medium mb-2">How Payment Processing Works</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Customer completes Stripe checkout</li>
                    <li>Stripe sends webhook to our server (may fail on preview URLs)</li>
                    <li>If webhook fails, customer lands on success page which polls for status</li>
                    <li>Success page verifies payment with Stripe and activates subscription</li>
                  </ol>
                </div>
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="text-yellow-400 font-medium mb-2">If Payment is Stuck</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Check Stripe Dashboard to confirm payment was received</li>
                    <li>Click "Verify with Stripe" to attempt automatic verification</li>
                    <li>If that fails, click "Force Activate" after manual confirmation</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'businesses' && <ConnectionRequests token={token} />}
        {activeTab === 'businesses' && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900">All Businesses</CardTitle>
              <CardDescription>Click on a business to view details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Business</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Category</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Tier</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Minutes Used</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Texts Used</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Pro Number</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Converted By</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.map(biz => (
                      <tr 
                        key={biz.id} 
                        className="border-b border-slate-200/50 hover:bg-slate-100/50 cursor-pointer transition-colors"
                        onClick={() => handleViewBusiness(biz)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                              <div className="text-slate-900 font-medium">{biz.business_name}</div>
                              <div className="text-slate-400 text-sm">{biz.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600 capitalize">{biz.business_category?.replace('_', ' ') || '-'}</td>
                        <td className="py-3 px-4">
                          <select
                            value={biz.subscription_tier || 'none'}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleUpdateTier(biz.id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-slate-100 text-slate-900 border border-gray-700 rounded px-2 py-1 text-sm"
                          >
                            <option value="none">None</option>
                            <option value="free">Trial</option>
                            <option value="starter">Starter</option>
                            <option value="pro">Pro</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{biz.call_minutes_used || 0}</td>
                        <td className="py-3 px-4 text-slate-600">{biz.sms_used || 0}</td>
                        <td className="py-3 px-4">
                          <BusinessNumberCell biz={biz} token={token} onUpdated={(id, upd) => setBusinesses(prev => prev.map(b => b.id === id ? { ...b, ...upd } : b))} />
                        </td>
                        <td className="py-3 px-4 text-sm" data-testid={`converted-by-${biz.id}`}>
                          {biz.converted_by_rep_name ? <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{biz.converted_by_rep_name}</span> : <span className="text-slate-400">-</span>}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewBusiness(biz);
                              }}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/business/${biz.id}`);
                              }}
                              className="text-green-400 hover:text-green-300 hover:bg-green-900/20"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBusiness(biz.id);
                              }}
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {businesses.length === 0 && (
                  <p className="text-slate-400 text-center py-8">No businesses found</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'users' && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900">All Users</CardTitle>
              <CardDescription>Click on a user to view details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">User</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Role</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Created</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr 
                        key={u.id} 
                        className="border-b border-slate-200/50 hover:bg-slate-100/50 cursor-pointer transition-colors"
                        onClick={() => handleViewUser(u)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                              <Users className="w-5 h-5 text-blue-400" />
                            </div>
                            <span className="text-slate-900">{u.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{u.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            u.role === 'admin' 
                              ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                              : u.role === 'sales_rep'
                              ? 'bg-blue-100 text-blue-700 border border-blue-200'
                              : 'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}>
                            {u.role || 'user'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-400 text-sm">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewUser(u);
                            }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sales Reps Tab */}
        {activeTab === 'sales_reps' && <RepLeaderboard token={token} />}
        {activeTab === 'sales_reps' && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-slate-900">Sales Representatives</CardTitle>
                <CardDescription>Manage sales rep accounts for live demos</CardDescription>
              </div>
              <Button 
                onClick={() => setShowCreateSalesRep(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Sales Rep
              </Button>
            </CardHeader>
            <CardContent>
              {/* Create Sales Rep Form */}
              {showCreateSalesRep && (
                <div className="mb-6 p-4 bg-slate-100 rounded-lg border border-gray-700">
                  <h3 className="text-slate-900 font-medium mb-4">Create New Sales Rep</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-slate-500 mb-1">Name</label>
                      <Input 
                        value={newSalesRep.name}
                        onChange={(e) => setNewSalesRep({...newSalesRep, name: e.target.value})}
                        placeholder="John Smith"
                        className="bg-white border-slate-300 text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-500 mb-1">Email *</label>
                      <Input 
                        type="email"
                        value={newSalesRep.email}
                        onChange={(e) => setNewSalesRep({...newSalesRep, email: e.target.value})}
                        placeholder="sales@example.com"
                        className="bg-white border-slate-300 text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-500 mb-1">Temporary password (optional)</label>
                      <Input 
                        type="password"
                        value={newSalesRep.password}
                        onChange={(e) => setNewSalesRep({...newSalesRep, password: e.target.value})}
                        placeholder="Leave blank to generate one"
                        className="bg-white border-slate-300 text-slate-900"
                        data-testid="create-rep-password-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">We email it to them. They must change it on first sign-in.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleCreateSalesRep}
                      disabled={creatingSalesRep}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {creatingSalesRep ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowCreateSalesRep(false);
                        setNewSalesRep({ email: '', password: '', name: '' });
                      }}
                      className="border-gray-600 text-slate-600"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Sales Reps List */}
              {salesReps.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-slate-500">No sales reps yet</p>
                  <p className="text-slate-400 text-sm mt-1">Create sales rep accounts to give them access to the demo builder</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Name</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Email</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Signups</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Paid</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Created</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesReps.map(rep => (
                        <tr key={rep.id} className="border-b border-slate-200/50 hover:bg-slate-100/50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-400" />
                              </div>
                              <span className="text-slate-900">{rep.name || 'Unnamed'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{rep.email}</td>
                          <td className="py-3 px-4 text-slate-900 font-medium" data-testid={`rep-signups-${rep.id}`}>{rep.signups || 0}</td>
                          <td className="py-3 px-4 text-green-600 font-medium" data-testid={`rep-paid-${rep.id}`}>{rep.paid_conversions || 0}</td>
                          <td className="py-3 px-4 text-slate-400 text-sm">
                            {rep.created_at ? new Date(rep.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSalesRep(rep.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Info Box */}
              <div className="mt-6 p-4 bg-blue-900/20 border border-blue-800 rounded-lg">
                <h4 className="text-blue-400 font-medium mb-2">How Sales Rep Accounts Work</h4>
                <ul className="text-sm text-slate-500 space-y-1">
                  <li>• Sales reps can access the Demo Builder at <span className="text-blue-300">/sales</span></li>
                  <li>• They use the shared demo phone number: <span className="text-green-400">+1 (937) 744-4837</span></li>
                  <li>• They can create Pro demos for prospects on the spot</li>
                  <li>• They cannot access admin stats, profits, or customer data</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'calls' && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900">Recent Calls</CardTitle>
              <CardDescription>Click on a call to view details and transcript</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Call ID</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Business</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Duration</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.map(call => (
                      <tr 
                        key={call.call_id || call.id} 
                        className="border-b border-slate-200/50 hover:bg-slate-100/50 cursor-pointer transition-colors"
                        onClick={() => handleViewCall(call)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                              <Phone className="w-5 h-5 text-green-400" />
                            </div>
                            <span className="text-slate-600 font-mono text-sm">
                              {(call.call_id || call.id || '').slice(0, 12)}...
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-900">{call.business_name || '-'}</td>
                        <td className="py-3 px-4 text-slate-600">{call.duration || 0} mins</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            call.status === 'ended' || call.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                            call.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                            'bg-yellow-500/20 text-yellow-300'
                          }`}>
                            {call.status || 'unknown'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-400 text-sm">
                          {call.created_at ? new Date(call.created_at).toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCall(call);
                            }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {calls.length === 0 && (
                  <p className="text-slate-400 text-center py-8">No calls found</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Business Detail Modal */}
      {selectedBusiness && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <Card className="bg-gray-900 border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <CardTitle className="text-slate-900">{selectedBusiness.business_name}</CardTitle>
                    <CardDescription>{selectedBusiness.email}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditMode(!editMode)}
                    className="text-blue-400 hover:bg-blue-900/20"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    {editMode ? 'Cancel' : 'Edit'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={closeModal} className="text-slate-500">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Business Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-500 text-sm">Business Name</label>
                  {editMode ? (
                    <Input
                      value={editData.business_name || ''}
                      onChange={(e) => setEditData({...editData, business_name: e.target.value})}
                      className="bg-slate-100 border-gray-700 text-slate-900 mt-1"
                    />
                  ) : (
                    <p className="text-slate-900">{selectedBusiness.business_name}</p>
                  )}
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Category</label>
                  {editMode ? (
                    <Input
                      value={editData.business_category || ''}
                      onChange={(e) => setEditData({...editData, business_category: e.target.value})}
                      className="bg-slate-100 border-gray-700 text-slate-900 mt-1"
                    />
                  ) : (
                    <p className="text-slate-900 capitalize">{selectedBusiness.business_category?.replace('_', ' ') || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="text-slate-500 text-sm flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                  <p className="text-slate-900">{selectedBusiness.email}</p>
                </div>
                <div>
                  <label className="text-slate-500 text-sm flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</label>
                  {editMode ? (
                    <Input
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({...editData, phone: e.target.value})}
                      className="bg-slate-100 border-gray-700 text-slate-900 mt-1"
                    />
                  ) : (
                    <p className="text-slate-900">{selectedBusiness.phone || '-'}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="text-slate-500 text-sm flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</label>
                  {editMode ? (
                    <Input
                      value={editData.address || ''}
                      onChange={(e) => setEditData({...editData, address: e.target.value})}
                      className="bg-slate-100 border-gray-700 text-slate-900 mt-1"
                    />
                  ) : (
                    <p className="text-slate-900">{selectedBusiness.address || '-'} {selectedBusiness.city} {selectedBusiness.state}</p>
                  )}
                </div>
              </div>

              {/* Subscription Info */}
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-slate-900 font-medium mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-yellow-400" /> Subscription
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-100/50 rounded-lg p-3">
                    <p className="text-slate-500 text-sm">Plan</p>
                    <p className="text-slate-900 font-medium capitalize">{selectedBusiness.subscription_tier || 'None'}</p>
                  </div>
                  <div className="bg-slate-100/50 rounded-lg p-3">
                    <p className="text-slate-500 text-sm">Minutes Used</p>
                    <p className="text-slate-900 font-medium">{selectedBusiness.call_minutes_used || 0}</p>
                  </div>
                  <div className="bg-slate-100/50 rounded-lg p-3">
                    <p className="text-slate-500 text-sm">Texts Used</p>
                    <p className="text-slate-900 font-medium">{selectedBusiness.sms_used || 0}</p>
                  </div>
                </div>
              </div>

              {/* Phone Number Assignment */}
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-slate-900 font-medium mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-400" /> Phone Number Assignment
                </h3>
                <div className="bg-slate-100/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-slate-500">Current Pro Number:</span>
                    <span className="text-slate-900 font-mono">
                      {selectedBusiness.retell_phone_number || selectedBusiness.phone_number || 'Not assigned'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-slate-500">SMS Number:</span>
                    <span className="text-slate-900 font-mono">
                      {selectedBusiness.sms_phone_number || 'Not assigned'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-700">
                    <p className="text-slate-500 text-xs mb-2">
                      Manually assign a phone number you've purchased from Retell/Twilio:
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={assignPhoneNumber}
                        onChange={(e) => setAssignPhoneNumber(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="bg-white border-slate-300 text-slate-900 flex-1"
                        data-testid="assign-phone-input"
                      />
                      <Button
                        onClick={handleAssignPhoneNumber}
                        disabled={!assignPhoneNumber || assigningPhone}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="assign-phone-btn"
                      >
                        {assigningPhone ? 'Assigning...' : 'Assign'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Created & Updated */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <span className="text-slate-900 ml-2">
                      {selectedBusiness.created_at ? new Date(selectedBusiness.created_at).toLocaleString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">ID:</span>
                    <span className="text-slate-600 ml-2 font-mono text-xs">{selectedBusiness.id}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="border-t border-slate-200 pt-4 flex justify-between">
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate(`/business/${selectedBusiness.id}`)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> View Dashboard
                  </Button>
                  {editMode && (
                    <Button onClick={handleSaveBusiness} className="bg-green-600 hover:bg-green-700">
                      Save Changes
                    </Button>
                  )}
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    handleDeleteBusiness(selectedBusiness.id);
                    closeModal();
                  }}
                  className="text-red-400 hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <Card className="bg-gray-900 border-gray-700 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-slate-900">{selectedUser.name || 'Unknown'}</CardTitle>
                    <CardDescription>{selectedUser.email}</CardDescription>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={closeModal} className="text-slate-500">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-500 text-sm">Role</label>
                  <p className="text-slate-900 capitalize">{selectedUser.role || 'user'}</p>
                </div>
                <div>
                  <label className="text-slate-500 text-sm">ID</label>
                  <p className="text-slate-600 font-mono text-xs">{selectedUser.id}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-slate-500 text-sm">Email</label>
                  <p className="text-slate-900">{selectedUser.email}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-slate-500 text-sm">Created</label>
                  <p className="text-slate-900">
                    {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Call Detail Modal */}
      {selectedCall && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <Card className="bg-white border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <CardTitle className="text-slate-900">Call Details</CardTitle>
                    <CardDescription className="font-mono">{selectedCall.call_id || selectedCall.id}</CardDescription>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={closeModal} className="text-slate-500">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-100/50 rounded-lg p-3">
                  <p className="text-slate-500 text-sm">Duration</p>
                  <p className="text-slate-900 font-medium text-xl">{selectedCall.duration || 0} mins</p>
                </div>
                <div className="bg-slate-100/50 rounded-lg p-3">
                  <p className="text-slate-500 text-sm">Status</p>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    selectedCall.status === 'ended' || selectedCall.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                    selectedCall.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                    'bg-yellow-500/20 text-yellow-300'
                  }`}>
                    {selectedCall.status || 'unknown'}
                  </span>
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Business</label>
                  <p className="text-slate-900">{selectedCall.business_name || '-'}</p>
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Date</label>
                  <p className="text-slate-900">
                    {selectedCall.created_at ? new Date(selectedCall.created_at).toLocaleString() : '-'}
                  </p>
                </div>
              </div>

              {/* Call Recording Playback */}
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-slate-900 font-medium mb-3 flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-green-400" />
                  Call Recording
                </h3>
                {selectedCall.recording_url ? (
                  <div className="bg-slate-100/50 rounded-lg p-4">
                    <audio 
                      controls 
                      className="w-full"
                      data-testid="call-recording-player"
                    >
                      <source src={selectedCall.recording_url} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                    <div className="flex items-center justify-between mt-3 text-sm">
                      <a 
                        href={selectedCall.recording_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" />
                        Download Recording
                      </a>
                      {selectedCall.public_log_url && (
                        <a 
                          href={selectedCall.public_log_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-violet-400 hover:text-violet-300 flex items-center gap-1"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Full Log
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-100/50 rounded-lg p-4 text-center">
                    <Mic className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">No recording available for this call</p>
                    <p className="text-gray-600 text-xs mt-1">Recordings are available after the call is analyzed</p>
                  </div>
                )}
              </div>

              {/* Transcript if available */}
              {selectedCall.transcript && (
                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-slate-900 font-medium mb-3">Transcript</h3>
                  <div className="bg-slate-100/50 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <pre className="text-slate-600 text-sm whitespace-pre-wrap">{selectedCall.transcript}</pre>
                  </div>
                </div>
              )}

              {/* Summary if available */}
              {selectedCall.summary && (
                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-slate-900 font-medium mb-3">Pro Summary</h3>
                  <p className="text-slate-600">{selectedCall.summary}</p>
                </div>
              )}

              {/* Call Analysis if available */}
              {selectedCall.call_analysis && Object.keys(selectedCall.call_analysis).length > 0 && (
                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-slate-900 font-medium mb-3">Call Analysis</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedCall.call_analysis.user_sentiment && (
                      <div className="bg-slate-100/50 rounded-lg p-3">
                        <p className="text-slate-500 text-xs">Sentiment</p>
                        <p className={`font-medium ${
                          selectedCall.call_analysis.user_sentiment === 'Positive' ? 'text-green-400' :
                          selectedCall.call_analysis.user_sentiment === 'Negative' ? 'text-red-400' :
                          'text-yellow-400'
                        }`}>{selectedCall.call_analysis.user_sentiment}</p>
                      </div>
                    )}
                    {selectedCall.call_analysis.call_successful !== undefined && (
                      <div className="bg-slate-100/50 rounded-lg p-3">
                        <p className="text-slate-500 text-xs">Successful</p>
                        <p className={selectedCall.call_analysis.call_successful ? 'text-green-400' : 'text-red-400'}>
                          {selectedCall.call_analysis.call_successful ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin Pro Chatbot */}
      {!chatOpen ? (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-50"
          data-testid="admin-chat-btn"
        >
          <Bot className="w-7 h-7 text-slate-900" />
        </button>
      ) : (
        <div 
          className={`fixed right-6 bottom-6 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 transition-all ${
            chatMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
          }`}
        >
          {/* Chat Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-700 bg-gradient-to-r from-violet-500/20 to-cyan-500/20 rounded-t-xl">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-cyan-400" />
              <span className="font-medium text-slate-900 text-sm">Admin Assistant</span>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setChatMinimized(!chatMinimized)}
                className="p-1 hover:bg-white/10 rounded"
              >
                {chatMinimized ? (
                  <Maximize2 className="w-4 h-4 text-slate-500" />
                ) : (
                  <Minimize2 className="w-4 h-4 text-slate-500" />
                )}
              </button>
              <button 
                onClick={() => setChatOpen(false)}
                className="p-1 hover:bg-white/10 rounded"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          {!chatMinimized && (
            <>
              <div className="flex-1 overflow-y-auto p-4 h-[380px] space-y-3">
                {chatMessages.map((msg, i) => (
                  <div 
                    key={i}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-[85%] p-3 rounded-lg text-sm ${
                        msg.role === 'user' 
                          ? 'bg-violet-500/30 text-slate-900' 
                          : 'bg-slate-100 text-gray-200'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 p-3 rounded-lg">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-3 border-t border-gray-700">
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleChatSend()}
                    placeholder="Ask me anything..."
                    className="flex-1 bg-slate-100 border-gray-700 text-slate-900 text-sm"
                    data-testid="admin-chat-input"
                  />
                  <Button 
                    onClick={handleChatSend}
                    disabled={chatLoading || !chatInput.trim()}
                    size="sm"
                    className="bg-violet-500 hover:bg-violet-600"
                    data-testid="admin-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
